import { useState, useEffect, useCallback } from "react";
import { SPECIALIST_HOSTNAME } from "env-vars";

export interface ClinicalTrialMatch {
  nctId: string;
  title: string;
  status: string;
  phase: string;
  sponsor: string;
  briefSummary: string;
  conditions: string[];
  matchScore: number;
  eligible: boolean;
}

interface ClinicalTrialMatchesResult {
  trials: ClinicalTrialMatch[];
  total: number;
  isLoading: boolean;
  error: string | null;
}

export function useClinicalTrialMatches(): ClinicalTrialMatchesResult {
  const [trials, setTrials] = useState<ClinicalTrialMatch[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatches = useCallback(async () => {
    const patientId = localStorage.getItem("ciq_id");
    const idToken = localStorage.getItem("id_token");

    if (!patientId || !idToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/clinical_trial_matches`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTrials(data.trials || []);
        setTotal(data.total || 0);
      } else {
        console.warn("Clinical trial matches request failed:", response.status);
        setTrials([]);
        setTotal(0);
      }
    } catch (err) {
      console.warn("Clinical trial matches fetch error:", err);
      setError("Unable to load clinical trial matches");
      setTrials([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  return { trials, total, isLoading, error };
}
